"use client";

import { Copy, Loader2, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type BibStrings,
  useBibStrings,
} from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SavedCitation } from "@/lib/bibliography";
import type { CitationLocatorLabel } from "@/lib/citation";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import { OUTPUT_LOCALES } from "@/lib/output-locales";

const COMMON_LABELS: CitationLocatorLabel[] = [
  "page",
  "chapter",
  "section",
  "paragraph",
  "line",
  "verse",
];

interface LocatorDraft {
  label: CitationLocatorLabel;
  value: string;
}

function labelText(bib: BibStrings): Record<CitationLocatorLabel, string> {
  return {
    page: bib.locatorPage,
    chapter: bib.locatorChapter,
    section: bib.locatorSection,
    paragraph: bib.locatorParagraph,
    line: bib.locatorLine,
    verse: bib.locatorVerse,
    figure: "Figure",
    table: "Table",
    note: "Note",
    part: "Part",
    volume: bib.fieldVolume,
    issue: bib.fieldIssue,
    book: "Book",
    column: "Column",
    folio: "Folio",
    opus: "Opus",
    "sub verbo": "Sub verbo",
  };
}

function initialLocators(
  citations: SavedCitation[]
): Record<string, LocatorDraft> {
  return Object.fromEntries(
    citations.map((citation) => [
      citation.id,
      { label: "page" as const, value: "" },
    ])
  );
}

interface CitationClusterComposerProps {
  citations: SavedCitation[];
  lang: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  style: string;
  styleName: string;
}

export function CitationClusterComposer({
  citations,
  lang,
  onOpenChange,
  open,
  style,
  styleName,
}: CitationClusterComposerProps) {
  const bib = useBibStrings();
  const [locators, setLocators] = useState<Record<string, LocatorDraft>>({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLocators(initialLocators(citations));
      setResult(null);
    }
  }, [citations, open]);

  const localeName =
    OUTPUT_LOCALES.find((locale) => locale.code === lang)?.label ?? lang;

  function updateLocator(id: string, patch: Partial<LocatorDraft>) {
    setLocators((current) => ({
      ...current,
      [id]: { ...(current[id] ?? { label: "page", value: "" }), ...patch },
    }));
  }

  async function buildAndCopy() {
    setLoading(true);
    setResult(null);
    try {
      const cluster = citations.map((citation) => {
        const locator = locators[citation.id];
        const value = locator?.value.trim();
        return {
          entry: citation.entry,
          locator: value ? { label: locator.label, value } : undefined,
        };
      });
      const response = await fetch("/api/cite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cluster, style, lang }),
      });
      if (!response.ok) {
        throw new Error(bib.clusterFailed);
      }
      const data = (await response.json()) as { inText?: string };
      const inText = data.inText?.trim();
      if (!inText) {
        throw new Error(bib.clusterFailed);
      }
      setResult(inText);
      await writeToClipboardOrThrow(inText);
      toast.success(bib.clusterCopied);
    } catch {
      toast.error(bib.clusterFailed);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{bib.clusterTitle}</DialogTitle>
          <DialogDescription>{bib.clusterDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="rounded-md bg-muted/40 px-3 py-2 text-muted-foreground text-sm">
            {bib.clusterTarget}: {styleName} · {localeName}
          </p>
          <div className="max-h-[45vh] space-y-3 overflow-y-auto">
            {citations.map((citation) => {
              const locator = locators[citation.id] ?? {
                label: "page" as const,
                value: "",
              };
              return (
                <div
                  className="space-y-2 rounded-lg border border-border/60 p-3"
                  key={citation.id}
                >
                  <p className="line-clamp-2 font-medium text-sm">
                    {citation.entry.title || citation.citation}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)]">
                    <Select
                      onValueChange={(value) =>
                        updateLocator(citation.id, {
                          label: value as CitationLocatorLabel,
                        })
                      }
                      value={locator.label}
                    >
                      <SelectTrigger
                        aria-label={bib.locatorType}
                        className="h-9 w-full"
                        size="sm"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_LABELS.map((label) => (
                          <SelectItem key={label} value={label}>
                            {labelText(bib)[label]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      aria-label={bib.locator}
                      className="h-9 flex-1"
                      maxLength={40}
                      onChange={(event) =>
                        updateLocator(citation.id, {
                          value: event.target.value,
                        })
                      }
                      placeholder={bib.clusterLocatorOptional}
                      value={locator.value}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {result ? (
            <output className="block rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm">
              {result}
            </output>
          ) : null}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline">
            {bib.cancel}
          </Button>
          <Button disabled={loading} onClick={buildAndCopy}>
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Copy className="size-4" />
            )}
            {loading ? bib.clusterBuilding : bib.clusterBuildCopy}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CitationClusterButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  const bib = useBibStrings();
  return (
    <Button disabled={disabled} onClick={onClick} size="sm" variant="outline">
      <Quote className="size-4" />
      {bib.clusterOpen}
    </Button>
  );
}
